import React, { useState, useEffect, useMemo } from 'react';
import { TextV2, Box, Flex, JoyKitThemeProvider, SelectV1, OptionType, ButtonV2, FormControl, InputV2, DrawerV2, DialogV2 } from '@withjoy/joykit';
import 'react-intl-tel-input/dist/main.css';
import { useTranslation } from '@shared/core';
import { useReminderDurationOptions, DEV_REMINDER_MINUTES_PER_WEEK } from './ReminderModal.constants';
import { addWeeks, isAfter } from 'date-fns';
import { JOY_TERMS_URL, JOY_PRIVACY_URL } from '@shared/utils/constants';
import { useCreateRemindMeLaterEmailMutation } from '@graphql/generated';
import { useGuestData } from '@shared/hooks/useGuestData';
import { CheckOutline, HotelProduct, Close } from '@withjoy/joykit/icons';
import { getConfig } from '@config';
import { pxToRem } from '@withjoy/joykit/theme';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useEventInfo } from '@shared/utils/eventInfo';
import * as Yup from 'yup';

// Define the telemetry interface for the modal
interface ReminderModalTelemetry {
  remindMeLaterDialog: {
    enter: () => void;
  };
  formFieldInteracted: (label: string, inputAction: 'onFocus' | 'onChange') => void;
  remindMeLaterFormSubmitted: (duration: string, reminderDate: string) => void;
  remindMeLaterDialogDismissed: () => void;
  remindMeLaterDialogOutsideClick: () => void;
}

interface ReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  telemetry: ReminderModalTelemetry;
  // Custom offset for positioning
  customOffset?: {
    x: number;
    y: number;
  };
  showCloseButton?: boolean;
  showNoThanksButton?: boolean;
}

// Validation schema using Yup
const createEmailValidationSchema = (emailErrorText: () => string) =>
  Yup.object({
    email: Yup.string().email(emailErrorText()).required(emailErrorText()),
  });

export const ReminderModal: React.FC<ReminderModalProps> = ({ isOpen, onClose, eventId, telemetry, customOffset, showCloseButton = true, showNoThanksButton = true }) => {
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } });
  const { t } = useTranslation('guestSiteCustom');
  const reminderModal = t('reminderModal');
  const { title, subtitle, terms, privacyPolicy, consentTextEmail, sendReminder, emailPlaceholder, emailErrorText, setReminder, noThanks, emailLabel, success } = reminderModal;
  const allReminderDurationOptions = useReminderDurationOptions();

  const [createRemindMeLaterEmail, { loading, data }] = useCreateRemindMeLaterEmailMutation();

  const { person } = useGuestData({ eventId });
  const { finalizedEventDate } = useEventInfo();

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const isDevelopment = getConfig().environment === 'dev';

  // Filter week options based on event date
  const filteredReminderDurationOptions = useMemo(() => {
    if (!finalizedEventDate) {
      return allReminderDurationOptions;
    }

    const now = new Date();
    const sixDaysBeforeEvent = new Date(finalizedEventDate);
    sixDaysBeforeEvent.setDate(sixDaysBeforeEvent.getDate() - 6);

    return allReminderDurationOptions.filter(option => {
      const weekValue = parseInt(option.value as string);
      const reminderDate = addWeeks(now, weekValue);

      // Only show options where the reminder date is at least 6 days before the event date
      return !isAfter(reminderDate, sixDaysBeforeEvent);
    });
  }, [allReminderDurationOptions, finalizedEventDate]);

  const [selectedWeek, setSelectedWeek] = useState<OptionType | null>(filteredReminderDurationOptions[0]);

  useEffect(() => {
    if (person?.email) {
      setEmail(person.email);
    }
  }, [person]);

  // Track page view when modal is opened
  useEffect(() => {
    if (isOpen) {
      telemetry.remindMeLaterDialog.enter();
    }
  }, [isOpen, telemetry.remindMeLaterDialog]);

  useEffect(() => {
    if (filteredReminderDurationOptions.length > 0 && !filteredReminderDurationOptions.find(option => option.value === selectedWeek?.value)) {
      setSelectedWeek(filteredReminderDurationOptions[0]);
    }
  }, [filteredReminderDurationOptions, selectedWeek]);

  // Handle mutation success
  useEffect(() => {
    if (!loading && 'createRemindMeLaterEmail' in (data || {})) {
      setIsSuccess(true);
    }
  }, [loading, data]);

  const validateEmail = async (emailValue: string): Promise<string | undefined> => {
    try {
      const validationSchema = createEmailValidationSchema(emailErrorText);
      await validationSchema.validate({ email: emailValue }, { abortEarly: false });
      return undefined; // No error
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        return error.errors[0]; // Return first error message
      }
      throw error; // Re-throw unexpected errors
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailError) {
      setEmailError('');
    }
  };

  const handleEmailFocus = () => {
    telemetry.formFieldInteracted('email', 'onFocus');
  };

  const handleEmailBlur = async () => {
    if (email) {
      const error = await validateEmail(email);
      setEmailError(error || '');
    }
  };

  const handleSubmit = async () => {
    const emailValidationError = await validateEmail(email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      return;
    }

    const sendReminderValue = selectedWeek?.label as string;
    const weekValue = parseInt(selectedWeek?.value as string);

    let reminderDate: Date;
    if (isDevelopment) {
      // In development, use minutes instead of weeks
      const minutesToAdd = weekValue * DEV_REMINDER_MINUTES_PER_WEEK;
      reminderDate = new Date(Date.now() + minutesToAdd * 60 * 1000);
    } else {
      // In production, use weeks
      reminderDate = addWeeks(new Date(), weekValue);
    }

    telemetry.remindMeLaterFormSubmitted(sendReminderValue, reminderDate.toISOString());

    await createRemindMeLaterEmail({
      variables: {
        data: {
          email,
          eventId,
          reminderDate: reminderDate.toISOString(),
        },
      },
    });
  };

  const handleCancel = () => {
    telemetry.remindMeLaterDialogDismissed();
    onClose();
  };

  const handleSuccessClose = () => {
    onClose();
  };

  const handleOutsideClick = () => {
    if (!isSuccess) {
      telemetry.remindMeLaterDialogOutsideClick();
    }
    onClose();
  };
  const renderContent = () => {
    if (isSuccess) {
      const weekValue = parseInt(selectedWeek?.value as string);
      return (
        <Flex
          flexDirection="column"
          gap={5}
          alignItems="center"
          textAlign="center"
        >
          <CheckOutline size={36} />
          <Flex
            flexDirection="column"
            gap={3}
          >
            <TextV2
              typographyVariant="hed5"
              fontWeight="semibold"
            >
              {success.title()}
            </TextV2>
            <TextV2 typographyVariant="body3">{success.subtitle.text({ count: weekValue })}</TextV2>
          </Flex>
        </Flex>
      );
    }

    return (
      <Flex
        flexDirection="column"
        gap={6}
      >
        <Flex
          flexDirection="column"
          gap={5}
          alignItems="center"
        >
          <HotelProduct size={48} />
          <Flex
            flexDirection="column"
            gap={3}
            textAlign="center"
          >
            <TextV2
              typographyVariant="hed5"
              fontWeight="semibold"
            >
              {title()}
            </TextV2>
            <TextV2 typographyVariant="body3">{subtitle()}</TextV2>
          </Flex>
        </Flex>
        <Flex
          flexDirection="column"
          gap={5}
        >
          <Box
            display="flex"
            flexDirection="column"
            gap={3}
          >
            <TextV2 typographyVariant="label3">{sendReminder()}</TextV2>
            <SelectV1
              searchable={false}
              options={filteredReminderDurationOptions}
              value={selectedWeek}
              onMenuOpen={() => {
                telemetry.formFieldInteracted('send reminder', 'onFocus');
              }}
              onChange={value => {
                setSelectedWeek(value);
              }}
            />
          </Box>

          <FormControl
            label={emailLabel()}
            error={emailError}
            overrides={{ Hint: { props: { color: 'negative6' } } }}
          >
            <InputV2
              type="email"
              name="email"
              id="email"
              value={email}
              onChange={e => handleEmailChange(e.target.value)}
              onBlur={handleEmailBlur}
              onFocus={handleEmailFocus}
              autoComplete="email"
              placeholder={emailPlaceholder()}
            />
          </FormControl>

          <TextV2
            typographyVariant="label1"
            color="mono10"
            letterSpacing={-0.032}
            lineHeight={1.4}
            textAlign="center"
          >
            {consentTextEmail({
              terms: (
                // eslint-disable-next-line react/jsx-no-target-blank
                <a
                  href={JOY_TERMS_URL}
                  target="_blank"
                  style={{ textDecoration: 'underline' }}
                >
                  {terms()}
                </a>
              ),
              privacyPolicy: (
                // eslint-disable-next-line react/jsx-no-target-blank
                <a
                  href={JOY_PRIVACY_URL}
                  target="_blank"
                  style={{ textDecoration: 'underline' }}
                >
                  {privacyPolicy()}
                </a>
              ),
            })}
          </TextV2>
        </Flex>
      </Flex>
    );
  };

  // Render footer buttons
  const renderFooter = () => {
    if (isSuccess) {
      return (
        <ButtonV2
          variant="solid"
          intent="newPrimary"
          shape="rounded"
          onClick={handleSuccessClose}
          width="100%"
          fontWeight="semibold"
        >
          {success.doneButton()}
        </ButtonV2>
      );
    }

    return (
      <Box
        display="flex"
        flexDirection="column"
        gap={5}
        width="100%"
      >
        <ButtonV2
          intent="newPrimary"
          onClick={handleSubmit}
          shape="rounded"
          width="100%"
          loading={loading}
          fontWeight="semibold"
        >
          {setReminder()}
        </ButtonV2>
        {showNoThanksButton && (
          <ButtonV2
            variant="ghost"
            intent="neutral"
            onClick={handleCancel}
            width="100%"
            fontWeight="semibold"
          >
            {noThanks()}
          </ButtonV2>
        )}
      </Box>
    );
  };

  // Mobile: use DrawerV2
  if (isMobile) {
    return (
      <JoyKitThemeProvider>
        <DrawerV2
          onClose={onClose}
          size="auto"
          isOpen={isOpen}
          anchor="bottom"
          useBackdrop
          overrides={{
            Content: {
              props: {
                height: 'unset',
                borderRadius: '0.5rem 0.5rem 0 0',
                width: '100vw',
                padding: '0',
                maxHeight: '100%',
                overflow: 'auto',
              },
            },
            CloseButton: {
              props: {
                paddingX: pxToRem(16),
                paddingY: pxToRem(16),
                top: 0,
                right: 0,
              },
            },
          }}
        >
          <DrawerV2.CloseButton />
          <DrawerV2.Body padding={0}>
            <Box
              backgroundColor={'white'}
              color={'inherit'}
              padding={6}
            >
              {renderContent()}
              <Box marginTop={6}>{renderFooter()}</Box>
            </Box>
          </DrawerV2.Body>
        </DrawerV2>
      </JoyKitThemeProvider>
    );
  }

  // Desktop: use DialogV2 with custom positioning for GuestGuide
  return (
    <JoyKitThemeProvider>
      {customOffset ? (
        // For GuestGuide: use a custom positioned modal
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={1600}
          backgroundColor="rgba(0, 0, 0, 0.5)"
          display={isOpen ? 'block' : 'none'}
          onClick={handleOutsideClick}
        >
          <Box
            position="fixed"
            bottom={customOffset.y}
            right={customOffset.x}
            backgroundColor="white"
            borderRadius={3}
            width={pxToRem(404)}
            maxWidth="90vw"
            maxHeight="90vh"
            overflow="auto"
            onClick={e => e.stopPropagation()}
            boxShadow="0px 4px 20px rgba(0, 0, 0, 0.15)"
            zIndex={1601}
          >
            {showCloseButton && (
              <ButtonV2
                position="absolute"
                top={2}
                right={2}
                intent="neutral"
                shape="rounded"
                variant="ghost"
                padding={1}
                onClick={onClose}
                cursor="pointer"
                zIndex={1602}
              >
                <Close size={16} />
              </ButtonV2>
            )}
            <Box padding={6}>{renderContent()}</Box>
            <Box
              marginTop={6}
              borderTop="1px solid"
              borderColor="mono5"
              padding={6}
            >
              {renderFooter()}
            </Box>
          </Box>
        </Box>
      ) : (
        // For default: use DialogV2 with natural centering
        <DialogV2
          isOpen={isOpen}
          onClose={handleOutsideClick}
          size="sm"
          overrides={{
            Content: {
              props: {
                maxWidth: pxToRem(404),
                width: 'auto',
              },
            },
            Body: {
              props: {
                padding: '1.5rem !important',
              },
            },
          }}
        >
          {showCloseButton && <DialogV2.CloseButton />}
          <DialogV2.Body>{renderContent()}</DialogV2.Body>
          <Box
            marginTop={6}
            borderTop="1px solid"
            borderColor="mono5"
            padding={6}
          >
            {renderFooter()}
          </Box>
        </DialogV2>
      )}
    </JoyKitThemeProvider>
  );
};
